
// @ts-check


import { mergeDonorCards } from './DonorCardMerger';
import { performQuerySpecApiRequestPromise } from './PerformQuerySpecApiRequest';
import { resolvePartialResultAtoms } from './ResolvePartialResultAtoms';


import CampaignList, { getQueryName, getCompositeQueryNames } from './CampaignList';



/**
 * @typedef {import('./coreTypes').ResultAtom} ResultAtom
 * @typedef {import('./coreTypes').ResultAtomPartial} ResultAtomPartial
 * @typedef {import('./coreTypes').QuerySpec} QuerySpec
 */

/**
 * @param {QuerySpec} querySpec 
 * @param {?number} chunkSize
 * @return {{ initial: Promise<ResultAtom[]>, getNext: function(): Promise<ResultAtom[]> }}
 */
function getResultsFor(querySpec, chunkSize) {

	const usedChunkSize = chunkSize ?? 300;
	let currentOffset = 0;

	const initial = 
		performQuerySpecApiRequestPromise(querySpec, currentOffset, usedChunkSize)
			.then(resolvePartialResultAtoms)
			.then(mergeDonorCards);

	const getNext = 
		() => {
			currentOffset += usedChunkSize;
			return performQuerySpecApiRequestPromise(querySpec, currentOffset, usedChunkSize)
				.then(resolvePartialResultAtoms)
				.then(mergeDonorCards);
		};

	return { initial: initial, getNext: getNext };
}


/**
 * @param {QuerySpec[]} querySpecList 
 * @param {?number} chunkSize
 * @return {{ initial: Promise<ResultAtom[]>, getNext: function(): Promise<ResultAtom[]> }}
 */
function getSyncedResultsFor(querySpecList, chunkSize) {

	/**
	 * @template T
	 * @param {T[][]} aa 
	 * @return {T[]}
	 */
	const interweaveFlatten = (aa) => {
		const res = [];	
		for (let k = 0; ; k++) {
			let numPassed = 0;
			for (let i = 0; i < aa.length; i++) {
				if (aa[i].length <= k) { numPassed++; continue; }
				res.push(aa[i][k]);
			}
			if (numPassed >= aa.length) break;
		}	
		return res;
	};

	const usedChunkSize = chunkSize ?? 300;
	let currentOffset = 0;

	let initialList = [];

	for (let a = 0; a < querySpecList.length; a++) {
		initialList.push(
			performQuerySpecApiRequestPromise(
				querySpecList[a], currentOffset, usedChunkSize));
	}

	const initial =
		Promise.all(initialList)
			.then(interweaveFlatten)
			.then(resolvePartialResultAtoms)
			.then(mergeDonorCards);


	const getNext = 
		() => {
			currentOffset += usedChunkSize;

			let nextList = [];

			for (let a = 0; a < querySpecList.length; a++) {
				nextList.push(
					performQuerySpecApiRequestPromise(
						querySpecList[a], currentOffset, usedChunkSize));
			}

			return (
				Promise.all(nextList)
					.then(interweaveFlatten)
					.then(resolvePartialResultAtoms)
					.then(mergeDonorCards));
		};

	return { initial: initial, getNext: getNext };
}



/**
 * @param {QuerySpec} querySpec 
 * @param {?number} chunkSize
 * @return {{ initial: Promise<ResultAtom[]>, getNext: function(): Promise<ResultAtom[]> }}
 */
function getResultsForCompositeCampaigns(querySpec, chunkSize) {
	if (querySpec.length !== 1) {
		throw new Error('Complex querySpecs (of length != 1) not supported.');
	}

	const tag = querySpec[0][0];
	const campName = querySpec[0][1];

	if (tag === 'campaign_name' || tag === 'campaign_search_string') {

		const campIndex = CampaignList.map(getQueryName).indexOf(campName);

		if (campIndex === -1) return getResultsFor(querySpec, chunkSize); // unknown

		const camp = CampaignList[campIndex];
		const componentNames = getCompositeQueryNames(camp);

		if (componentNames.length > 0) {
			/** @type {QuerySpec[]} */
			const qs = componentNames.map(n => [[tag, n]]);

			return getSyncedResultsFor(qs, chunkSize);
		}
		else return getResultsFor(querySpec, chunkSize);
	}
	else return getResultsFor(querySpec, chunkSize);
}



export { getResultsForCompositeCampaigns as getResultsFor, getSyncedResultsFor };